import React from 'react';
import { motion } from 'motion/react';
import { Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logoImg from "../../assets/97af715ab6a3414a5586695e213784f67e1e0ed5.png";
import aicteLogo from "../../assets/navbar_logo/aicte.png";
import makeInIndiaLogo from "../../assets/navbar_logo/make_in_india.png";
import moeLogo from "../../assets/navbar_logo/moe.png";

type NavbarProps = {
  onRequestDemo: () => void;
};

export const Navbar = ({ onRequestDemo }: NavbarProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleNavigateToProduct = (title: string) => {
    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setIsOpen(false);
    navigate(`/product/${slug}`);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-50/95 backdrop-blur-md border-b  shadow-sm">
      <div className="max-w-14xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 bg-white">
          <div className="flex items-center gap-2 md:gap-4">
            <button 
              onClick={handleLogoClick}
              className="cursor-pointer hover:opacity-80 transition-opacity"
              aria-label="Go to Anuvadini AI homepage"
            >
              <img 
                src={logoImg} 
                alt="Anuvadini AI - Make in India Global Translation Platform Logo" 
                className="h-20 md:h-16 object-contain"
                loading="eager"
              />
            </button>
            {/* Hide AICTE logos on mobile and small screens */}
            <div className="hidden lg:flex items-center gap-3">
              <img 
                src={aicteLogo} 
                alt="AICTE - All India Council for Technical Education Logo" 
                className="h-12 lg:h-16 object-contain"
                loading="eager"
              />
              <img 
                src={makeInIndiaLogo} 
                alt="Make in India - Government of India Initiative Logo" 
                className="h-10 lg:h-14 object-contain"
                loading="eager"
              />
              <img 
                src={moeLogo} 
                alt="Ministry of Education - Government of India Logo" 
                className="h-14 lg:h-20 object-contain"
                loading="eager"
              />
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-10">
            <button
              type="button"
              onClick={onRequestDemo}
              className="bg-green-400 hover:bg-green-500 text-white px-8 py-3 rounded-full font-black shadow-lg transition-all transform hover:-translate-y-0.5 active:scale-95"
              aria-label=" Request a Demo of Anuvadini AI Translation Platform"
            >
               Request a Demo
            </button>
          </div>

          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 p-2">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-gray-50 border-b border-gray-200 px-4 py-8 space-y-6 shadow-xl"
        >
          <div className="space-y-6">
            <div>
              <h4 className="font-bold mb-4 text-lg text-gray-800">Product Line</h4>
              <div className="space-y-3 pl-4">
                <button type="button" onClick={() => handleNavigateToProduct('Anuvadini Translation')} className="block text-left w-full text-gray-600 hover:text-[#f97316] transition-colors">Anuvadini Translation</button>
                <button type="button" onClick={() => handleNavigateToProduct('Anuvadini Print')} className="block text-left w-full text-gray-600 hover:text-[#f97316] transition-colors">Anuvadini Print</button>
                <button type="button" onClick={() => handleNavigateToProduct('Anuvadini GEO-AI')} className="block text-left w-full text-gray-600 hover:text-[#f97316] transition-colors">Anuvadini GEO-AI</button>
                <button type="button" onClick={() => handleNavigateToProduct('Anuvadini AI Agents')} className="block text-left w-full text-gray-600 hover:text-[#f97316] transition-colors">Anuvadini AI Agents</button>
                <button type="button" onClick={() => handleNavigateToProduct('Anuvadini Bharatt')} className="block text-left w-full text-gray-600 hover:text-[#f97316] transition-colors">Anuvadini Bharatt</button>
                <button type="button" onClick={() => handleNavigateToProduct('Anuvadini Web & Mobile')} className="block text-left w-full text-gray-600 hover:text-[#f97316] transition-colors">Anuvadini Web & Mobile</button>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-lg text-gray-800">Specialized Offerings</h4>
              <div className="space-y-3 pl-4">
                <button type="button" onClick={() => handleNavigateToProduct('Anuvadini Voice, Audio & Video')} className="block text-left w-full text-gray-600 hover:text-[#f97316] transition-colors">Anuvadini Voice, Audio & Video</button>
                <button type="button" onClick={() => handleNavigateToProduct('Anuvadini Games')} className="block text-left w-full text-gray-600 hover:text-[#f97316] transition-colors">Anuvadini Games</button>
                <button type="button" onClick={() => handleNavigateToProduct('Anuvadini Drafting & OCR')} className="block text-left w-full text-gray-600 hover:text-[#f97316] transition-colors">Anuvadini Drafting & OCR</button>
              </div>
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              onRequestDemo();
            }}
            className="w-full bg-green-400 hover:bg-green-500 text-white px-6 py-4 rounded-2xl font-black text-lg transition-colors"
          >
             Request a Demo
          </button>
        </motion.div>
      )}
    </nav>
  );
};
